import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState({ items: [], custom_boxes: [], subtotal: 0, count: 0, min_order: 40 });
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  const fetchCart = useCallback(async () => {
    if (!user) {
      setCart({ items: [], custom_boxes: [], subtotal: 0, count: 0, min_order: 40 });
      return;
    }
    
    try {
      setLoading(true);
      const response = await axios.get(`${API}/cart`, {
        withCredentials: true
      });
      setCart(response.data);
    } catch (error) {
      console.error('Error fetching cart:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  const addToCart = async (productId, quantity = 1, subscriptionType = 'one-time') => {
    try {
      await axios.post(`${API}/cart/add`, {
        product_id: productId,
        quantity,
        subscription_type: subscriptionType
      }, {
        withCredentials: true
      });
      await fetchCart();
    } catch (error) {
      throw error;
    }
  };

  const updateCartItem = async (productId, quantity, subscriptionType) => {
    try {
      await axios.put(`${API}/cart/${productId}`, {
        quantity,
        subscription_type: subscriptionType
      }, {
        withCredentials: true
      });
      await fetchCart();
    } catch (error) {
      throw error;
    }
  };

  const removeFromCart = async (productId) => {
    try {
      await axios.delete(`${API}/cart/${productId}`, {
        withCredentials: true
      });
      await fetchCart();
    } catch (error) {
      throw error;
    }
  };

  const clearCart = async () => {
    try {
      await axios.delete(`${API}/cart`, {
        withCredentials: true
      });
      await fetchCart();
    } catch (error) {
      throw error;
    }
  };

  const addCustomBox = async (tierKey, items) => {
    const res = await axios.post(`${API}/cart/custom-box`, { tier_key: tierKey, items }, { withCredentials: true });
    await fetchCart();
    return res.data;
  };

  const updateCustomBox = async (boxId, tierKey, items) => {
    const res = await axios.put(`${API}/cart/custom-box/${boxId}`, { tier_key: tierKey, items }, { withCredentials: true });
    await fetchCart();
    return res.data;
  };

  const removeCustomBox = async (boxId) => {
    await axios.delete(`${API}/cart/custom-box/${boxId}`, { withCredentials: true });
    await fetchCart();
  };

  return (
    <CartContext.Provider value={{
      cart,
      loading,
      addToCart,
      updateCartItem,
      removeFromCart,
      clearCart,
      addCustomBox,
      updateCustomBox,
      removeCustomBox,
      fetchCart
    }}>
      {children}
    </CartContext.Provider>
  );
};
